import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Calendar, MapPin, Users, Quote } from 'lucide-react';

const SpeechesSection = () => {
  const speeches = [
    {
      title: "Discours d'investiture",
      date: "2 avril 2024",
      location: "Palais de la République, Dakar",
      category: "Investiture",
      excerpt: "Je m'engage à me consacrer pleinement dans la refondation de nos institutions et le renforcement des fondements de notre vivre ensemble.",
      highlights: [
        "Refondation des institutions",
        "Renforcement du vivre ensemble",
        "Engagement pour la transparence"
      ],
      color: "bg-primary"
    },
    {
      title: "Discours à la Nation - 65ème anniversaire de l'Indépendance",
      date: "4 avril 2024",
      location: "Palais de la République",
      category: "Commémoration",
      excerpt: "Demain, nous célèbrerons ensemble le 65ème anniversaire de l'accession de notre pays à la souveraineté internationale.",
      highlights: [
        "Célébration de l'indépendance",
        "Hommage aux héros nationaux",
        "Vision pour l'avenir"
      ],
      color: "bg-secondary"
    },
    {
      title: "Présentation de la Vision Sénégal 2050",
      date: "14 octobre 2024",
      location: "Plateforme Jubbanti",
      category: "Vision",
      excerpt: "Le Sénégal 2050 : Agenda National de Transformation est bien plus qu'un simple plan de développement.",
      highlights: [
        "Transformation nationale",
        "Agenda 2050",
        "Développement durable"
      ],
      color: "bg-accent"
    },
    {
      title: "Discours sur l'intégration africaine",
      date: "2024",
      location: "CEDEAO",
      category: "International",
      excerpt: "Je lance un appel à nos frères et sœurs africains pour qu'ensemble nous consolidions les acquis obtenus dans les processus de construction de l'intégration.",
      highlights: [
        "Intégration africaine",
        "Coopération régionale",
        "Unité continentale"
      ],
      color: "bg-primary"
    },
    {
      title: "Ouverture de la 15ème Biennale de l'Art Africain",
      date: "2024",
      location: "Dakar",
      category: "Culture",
      excerpt: "L'Art, a-t-on coutume de dire, est un antidestin. Il contourne la finitude de la vie humaine pour inscrire l'œuvre dans l'éternité.",
      highlights: [
        "Promotion de l'art africain",
        "Patrimoine culturel",
        "Rayonnement international"
      ],
      color: "bg-secondary"
    },
    {
      title: "Discours de fin d'année",
      date: "31 décembre 2024",
      location: "Palais de la République",
      category: "Bilan",
      excerpt: "Message à la Nation pour dresser le bilan de l'année écoulée et présenter les perspectives pour l'année à venir.",
      highlights: [
        "Bilan annuel",
        "Perspectives d'avenir",
        "Remerciements au peuple"
      ],
      color: "bg-accent"
    }
  ];

  const quotes = [
    {
      text: "Je m'engage à me consacrer pleinement dans la refondation de nos institutions et le renforcement des fondements de notre vivre ensemble.",
      context: "Discours d'investiture"
    },
    {
      text: "Je lance un appel à nos frères et sœurs africains pour qu'ensemble nous consolidions les acquis obtenus dans les processus de construction de l'intégration.",
      context: "Sur l'intégration africaine"
    },
    {
      text: "L'Art est un antidestin. Il contourne la finitude de la vie humaine pour inscrire l'œuvre dans l'éternité.",
      context: "Biennale de l'Art Africain"
    }
  ];

  return (
    <section id="discours" className="py-20 bg-muted/30">
      <div className="container mx-auto px-4">
        <div className="max-w-6xl mx-auto">
          {/* En-tête de section */}
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-primary mb-6">
              Discours Importants
            </h2>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
              Découvrez les discours marquants du Président Bassirou Diomaye Faye, 
              porteurs de sa vision et de ses engagements pour le Sénégal.
            </p>
          </div>

          {/* Citations marquantes */}
          <div className="mb-16">
            <h3 className="text-2xl font-semibold text-center mb-8 text-primary">
              Citations marquantes
            </h3>
            <div className="grid md:grid-cols-3 gap-6">
              {quotes.map((quote, index) => (
                <Card key={index} className="shadow-card hover:shadow-elegant transition-all duration-300">
                  <CardContent className="p-6">
                    <Quote className="w-8 h-8 text-primary mb-4" />
                    <blockquote className="text-muted-foreground italic mb-4 leading-relaxed">
                      "{quote.text}"
                    </blockquote>
                    <cite className="text-sm font-semibold text-primary">
                      — {quote.context}
                    </cite>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>

          {/* Liste des discours */}
          <div className="space-y-6">
            <h3 className="text-2xl font-semibold text-center mb-8 text-primary">
              Discours officiels
            </h3>
            {speeches.map((speech, index) => (
              <Card key={index} className="shadow-card hover:shadow-elegant transition-all duration-300 transform hover:-translate-y-1">
                <CardContent className="p-8">
                  <div className="flex flex-col lg:flex-row lg:items-start gap-6">
                    {/* Informations principales */}
                    <div className="flex-1">
                      <div className="flex items-start justify-between mb-4">
                        <div>
                          <h4 className="text-xl font-semibold text-primary mb-2">
                            {speech.title}
                          </h4>
                          <div className="flex flex-wrap items-center gap-4 text-sm text-muted-foreground mb-3">
                            <div className="flex items-center gap-1">
                              <Calendar className="w-4 h-4" />
                              <span>{speech.date}</span>
                            </div>
                            <div className="flex items-center gap-1">
                              <MapPin className="w-4 h-4" />
                              <span>{speech.location}</span>
                            </div>
                          </div>
                        </div>
                        <Badge variant="outline" className="text-xs">
                          {speech.category}
                        </Badge>
                      </div>

                      <blockquote className="text-muted-foreground italic mb-4 pl-4 border-l-4 border-primary/30">
                        "{speech.excerpt}"
                      </blockquote>

                      {/* Points clés */}
                      <div className="mb-4">
                        <h5 className="font-semibold text-sm text-primary mb-2">Points clés :</h5>
                        <div className="flex flex-wrap gap-2">
                          {speech.highlights.map((highlight, highlightIndex) => (
                            <Badge key={highlightIndex} variant="secondary" className="text-xs">
                              {highlight}
                            </Badge>
                          ))}
                        </div>
                      </div>
                    </div>

                    {/* Indicateur visuel */}
                    <div className="flex-shrink-0">
                      <div className={`w-16 h-16 ${speech.color} rounded-full flex items-center justify-center text-white shadow-lg`}>
                        <Users className="w-8 h-8" />
                      </div>
                    </div>
                  </div>

                  {/* Bouton d'action */}
                  <div className="mt-6 pt-6 border-t">
                    <Button variant="outline" size="sm" className="w-full sm:w-auto">
                      Lire le discours complet
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Statistiques des discours */}
          <div className="mt-16 grid grid-cols-2 md:grid-cols-4 gap-6">
            <Card className="text-center shadow-card">
              <CardContent className="p-6">
                <div className="text-3xl font-bold text-primary mb-2">{speeches.length}</div>
                <div className="text-sm text-muted-foreground">Discours officiels</div>
              </CardContent>
            </Card>
            <Card className="text-center shadow-card">
              <CardContent className="p-6">
                <div className="text-3xl font-bold text-secondary mb-2">4</div>
                <div className="text-sm text-muted-foreground">Catégories</div>
              </CardContent>
            </Card>
            <Card className="text-center shadow-card">
              <CardContent className="p-6">
                <div className="text-3xl font-bold text-accent mb-2">2024</div>
                <div className="text-sm text-muted-foreground">Année d'investiture</div>
              </CardContent>
            </Card>
            <Card className="text-center shadow-card">
              <CardContent className="p-6">
                <div className="text-3xl font-bold text-primary mb-2">∞</div>
                <div className="text-sm text-muted-foreground">Impact national</div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </section>
  );
};

export default SpeechesSection;